"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SmartDropdown = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_jss_1 = __importDefault(require("react-jss"));
const lodash_uniqueid_1 = __importDefault(require("lodash.uniqueid"));
const hooks_1 = require("../../utils/hooks");
const text_1 = require("../../utils/text");
const icons_1 = require("../../icons");
const status_1 = require("../constants/status");
const keyEvents_1 = require("../constants/keyEvents");
const __1 = require("..");
const filteredItem_1 = __importDefault(require("./filteredItem"));
const smartDropdown_style_1 = __importDefault(require("./smartDropdown.style"));
const onTop_1 = __importDefault(require("./onTop"));
const SmartDropdown = ({ classes, id, dataCy = 'smartDropdown', options, value, onSelect, label, placeholder, noMatchMessage, compact = false, disabled = false, invalid = false, 
// @ts-ignore only used within the stylesheet
shadows = false, searchMode = false, embedList = false }) => {
    const usedId = react_1.useMemo(() => id || lodash_uniqueid_1.default('sdp_'), [id]);
    const wrapperRef = react_1.useRef(null);
    const inputRef = react_1.useRef(null);
    const [isFocused, setFocus] = react_1.useState(false);
    const [searchStr, setSearchStr] = react_1.useState('');
    const [activeOptionIdx, setActiveOptionIdx] = react_1.useState(0);
    // update the search string
    const updateSearchStr = react_1.useCallback(e => {
        const newValue = e.target.value;
        setSearchStr(newValue);
        setActiveOptionIdx(0);
    }, []);
    // highlight the current value in the list
    // if not set activate first option
    const activateCurrentValue = react_1.useCallback(() => {
        if (value !== undefined) {
            // find and activate the current value in the list
            const valueOptionIndex = options.findIndex(option => option.value === value);
            setActiveOptionIdx(Math.max(valueOptionIndex, 0));
        }
        else {
            // activate the first value in the list
            setActiveOptionIdx(0);
        }
    }, [value, options]);
    const focus = react_1.useCallback(() => {
        if (!disabled && !isFocused) {
            setFocus(true);
            activateCurrentValue();
            if (inputRef && inputRef.current)
                inputRef.current.focus();
        }
    }, [disabled, isFocused, activateCurrentValue]);
    const blur = react_1.useCallback(() => {
        setFocus(false);
        setActiveOptionIdx(0);
        setSearchStr('');
        if (inputRef && inputRef.current)
            inputRef.current.blur();
    }, []);
    const toggleFocus = react_1.useCallback(() => (isFocused ? blur() : focus()), [
        isFocused,
        focus,
        blur
    ]);
    // enable clicking outside the text input to close its options.
    hooks_1.useOnClickOutside(wrapperRef, blur);
    // close its options when pressing the escape key.
    hooks_1.useKeyPress(keyEvents_1.ESCAPE, blur);
    // select an option value
    const handleSelect = react_1.useCallback((value) => {
        onSelect(value);
        blur();
    }, [onSelect, blur]);
    // build the list of options to display when focused
    // filter based on current searchStr
    const filteredOptions = react_1.useMemo(() => {
        if (!isFocused)
            return [];
        return options.reduce((items, { value: optionValue, label: optionLabel, prefix: optionPrefix }) => {
            const transformedLabel = text_1.parseSearchOccurencesInStr(optionLabel, searchStr);
            if (transformedLabel) {
                items.push({
                    optionValue,
                    optionLabel,
                    optionPrefix,
                    transformedLabel
                });
            }
            return items;
        }, []);
    }, [isFocused, options, searchStr]);
    // navigate in options list using arrow.
    const navigateDown = react_1.useCallback(() => {
        if (filteredOptions.length &&
            activeOptionIdx < filteredOptions.length - 1) {
            setActiveOptionIdx(activeOptionIdx + 1);
        }
    }, [activeOptionIdx, filteredOptions]);
    hooks_1.useKeyPress(keyEvents_1.ARROW_DOWN, navigateDown);
    const navigateUp = react_1.useCallback(() => {
        if (filteredOptions.length && activeOptionIdx > 0) {
            setActiveOptionIdx(activeOptionIdx - 1);
        }
    }, [activeOptionIdx, filteredOptions]);
    hooks_1.useKeyPress(keyEvents_1.ARROW_UP, navigateUp);
    // on hit enter select active option.
    const selectActiveOptionOnEnter = react_1.useCallback(() => {
        if (filteredOptions.length) {
            handleSelect(filteredOptions[activeOptionIdx].optionValue);
        }
    }, [handleSelect, activeOptionIdx, filteredOptions]);
    hooks_1.useKeyPress(keyEvents_1.ENTER, selectActiveOptionOnEnter, inputRef.current);
    // value to display inside the input
    const { inputValue, inputPrefix } = react_1.useMemo(() => {
        if (isFocused)
            return { inputValue: searchStr };
        const currentOption = options.find(item => item.value === value);
        return {
            inputValue: currentOption === null || currentOption === void 0 ? void 0 : currentOption.label,
            inputPrefix: currentOption === null || currentOption === void 0 ? void 0 : currentOption.prefix
        };
    }, [isFocused, searchStr, options, value]);
    // placeholder to display inside the input
    const inputPlaceholder = react_1.useMemo(() => {
        // when focused check for the current value selected
        if (isFocused && value !== undefined) {
            const valueOption = options.find(option => option.value === value);
            if (valueOption)
                return valueOption.label;
        }
        // return the placeholder or label depending on compact or focused
        return isFocused ? placeholder || label : label || placeholder;
    }, [isFocused, value, options, placeholder, label]);
    const isSearchSet = !!searchStr;
    const isValueSet = !!value;
    const clearSearch = react_1.useCallback(() => {
        if (!disabled) {
            if (inputRef && inputRef.current) {
                inputRef.current.focus();
            }
            if (isSearchSet) {
                // if search string is not empty start by just deleting it without deleting the value
                setSearchStr('');
                activateCurrentValue();
            }
            else {
                // otherwise delete the value
                onSelect();
                setActiveOptionIdx(0);
            }
        }
    }, [disabled, onSelect, isSearchSet, activateCurrentValue]);
    const icon = react_1.useMemo(() => {
        if (searchMode) {
            return isSearchSet || isValueSet ? (jsx_runtime_1.jsx("span", Object.assign({ className: classes.clearIcon, onClick: clearSearch }, { children: jsx_runtime_1.jsx(icons_1.icons.Cross, { size: 25 }, void 0) }), void 0)) : (jsx_runtime_1.jsx("span", Object.assign({ className: classes.searchIcon }, { children: jsx_runtime_1.jsx(icons_1.icons.Search, { size: 25 }, void 0) }), void 0));
        }
        return (isSearchSet || isValueSet) && isFocused ? (jsx_runtime_1.jsx("span", Object.assign({ className: classes.clearIcon, onClick: clearSearch }, { children: jsx_runtime_1.jsx(icons_1.icons.Cross, { size: 25 }, void 0) }), void 0)) : (jsx_runtime_1.jsx("span", Object.assign({ className: classes.arrowIcon, "data-is-down": !isFocused, onClick: toggleFocus }, { children: jsx_runtime_1.jsx(icons_1.icons.ArrowMinTop, { size: 25 }, void 0) }), void 0));
    }, [
        searchMode,
        isSearchSet,
        isValueSet,
        isFocused,
        clearSearch,
        toggleFocus,
        classes
    ]);
    let status = null;
    if (invalid) {
        status = status_1.STATUS_INVALID;
    }
    const inputProps = {
        id: usedId,
        dataCy,
        value: inputValue || '',
        type: 'text',
        placeholder: inputPlaceholder,
        inputRef,
        prefix: inputPrefix,
        suffix: icon,
        handleChange: updateSearchStr,
        handleFocus: focus,
        status,
        disabled
    };
    let input = jsx_runtime_1.jsx(__1.Textinput, Object.assign({}, inputProps), void 0);
    if (compact && !!label) {
        const labelProps = {
            text: label,
            forId: usedId,
            inputHasFocus: isFocused,
            inputHasValue: isValueSet || !!inputValue,
            status
        };
        input = jsx_runtime_1.jsx(__1.Smartlabel, Object.assign({}, labelProps, { children: input }), void 0);
    }
    return (jsx_runtime_1.jsxs("div", Object.assign({ className: classes.root, ref: wrapperRef, "data-embed-list": embedList }, { children: [input, isFocused && (jsx_runtime_1.jsx(onTop_1.default, Object.assign({ refElement: wrapperRef.current }, { children: jsx_runtime_1.jsx("ul", Object.assign({ className: classes.optionsList, "data-has-shadows": shadows }, { children: !(filteredOptions === null || filteredOptions === void 0 ? void 0 : filteredOptions.length) ? (jsx_runtime_1.jsx("li", Object.assign({ className: classes.noMatch }, { children: noMatchMessage || 'No match...' }), void 0)) : (filteredOptions.map(({ optionValue, optionLabel, optionPrefix, transformedLabel }, idx) => (jsx_runtime_1.jsx(filteredItem_1.default, { index: idx, value: optionValue, label: optionLabel, prefix: optionPrefix, transformedLabel: transformedLabel, onSelect: handleSelect, isActive: idx === activeOptionIdx, activate: setActiveOptionIdx, isSelected: optionValue === value }, idx)))) }), void 0) }), void 0))] }), void 0));
};
exports.SmartDropdown = SmartDropdown;
exports.default = react_jss_1.default(smartDropdown_style_1.default)(react_1.memo(exports.SmartDropdown));
//# sourceMappingURL=index.js.map